import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  DollarSign, 
  Clock, 
  TrendingUp,
  Zap,
  Check,
  ArrowRight,
  Building2,
  FileText,
  RefreshCw,
  Shield,
  Loader2,
  ArrowLeft,
  AlertTriangle,
  Banknote,
  Receipt,
  PieChart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPageView, trackFormSubmit } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const painPoints = [
  {
    icon: Clock,
    title: 'Slow Reimbursements',
    description: '45-90 days to get paid by Medicaid. Cash flow nightmares for small operators trying to make payroll.'
  },
  {
    icon: FileText,
    title: 'Claims Complexity',
    description: '837 files, CMS1500 forms, denial codes. You need a billing department just to get paid for trips you already completed.'
  },
  {
    icon: DollarSign,
    title: 'High Processing Fees',
    description: 'Traditional payment processors charge 3-4% because they don\'t understand NEMT volume patterns.'
  },
  {
    icon: AlertTriangle,
    title: 'Driver Payment Delays',
    description: 'Drivers wait weeks for their cut while you wait for insurance reimbursement. Good drivers leave for faster-paying gigs.'
  }
];

const features = [
  {
    icon: Zap,
    title: 'Instant Driver Payouts',
    description: 'Pay drivers same-day or next-day, regardless of when you get reimbursed. We float the cash.',
    highlight: true
  },
  {
    icon: Receipt,
    title: 'Automated Claims',
    description: '837 Professional and CMS1500 generation. Auto-submit to Medicaid, MCOs, and commercial payers.'
  },
  {
    icon: RefreshCw,
    title: 'Real-Time Tracking',
    description: 'See every payment, every claim status, every denial reason in one dashboard. No more black boxes.'
  },
  {
    icon: PieChart,
    title: 'Revenue Analytics',
    description: 'Know your true cost per trip, profit margins by payer, and which contracts are actually making money.'
  },
  {
    icon: Shield,
    title: 'PCI Compliant',
    description: 'Bank-grade security for all transactions. We handle the compliance so you don\'t have to.'
  },
  {
    icon: Banknote,
    title: 'Multi-Payer Support',
    description: 'Medicaid, Medicare, private insurance, self-pay - all flowing through one unified system.'
  }
];

export const PaymentsPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    state: '',
    monthly_trip_volume: '',
    primary_payer_types: [],
    current_billing_method: '',
    interested_features: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const payerTypes = [
    'Medicaid', 'Medicare', 'Private Insurance', 'Brokers/MCOs', 'Self-Pay/Private', 'VA/Government'
  ];

  const featureInterests = [
    'Instant Driver Payouts', 'Automated Claims', 'Revenue Analytics', 'Payment Processing', 'All Features'
  ];

  // Track page view on mount
  useEffect(() => {
    trackPageView('/payments', 'medex_pay');
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/products/signup`, {
        ...formData,
        product: 'medex_pay',
        tags: ['payments', 'medex_pay', ...formData.interested_features.map(f => f.toLowerCase().replace(/ /g, '_'))],
        source: 'payments_landing_page'
      });
      
      setIsSubmitted(true);
      toast.success('Thank you! Our payments team will reach out shortly.');
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-medex-bg flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-8 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-neon-green" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-white mb-4">
            Request Received!
          </h2>
          <p className="text-slate-400 mb-6">
            Our payments team will review your information and reach out within 1-2 business days to discuss your needs.
          </p>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Return to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-medex-bg">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-blue/10 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-neon-blue/10 border border-neon-blue/30 mb-6">
                <CreditCard className="w-4 h-4 text-neon-blue" />
                <span className="text-sm text-neon-blue font-medium">Medex Pay</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                Get Paid <span className="text-neon-blue">Faster</span>, Pay Drivers <span className="text-neon-green">Instantly</span>
              </h1>
              
              <p className="text-xl text-slate-400 mb-8">
                Financial infrastructure built for NEMT. Automated claims, instant driver payouts, 
                and revenue analytics - all in one platform.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="#signup" className="btn-primary inline-flex items-center space-x-2">
                  <span>Get Started</span>
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#features" className="btn-secondary">
                  See Features
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="glass-card p-8 border-neon-blue/30">
                <h3 className="text-lg font-semibold text-white mb-6">The Medex Pay Difference</h3>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div>
                      <div className="text-sm text-slate-400">Traditional Reimbursement</div>
                      <div className="text-2xl font-bold text-red-400">45-90 days</div>
                    </div>
                    <ArrowRight className="w-6 h-6 text-slate-600" />
                    <div>
                      <div className="text-sm text-slate-400">With Medex Pay</div>
                      <div className="text-2xl font-bold text-neon-green">2-5 days</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div>
                      <div className="text-sm text-slate-400">Driver Wait Time</div>
                      <div className="text-2xl font-bold text-red-400">2+ weeks</div>
                    </div>
                    <ArrowRight className="w-6 h-6 text-slate-600" />
                    <div>
                      <div className="text-sm text-slate-400">Instant Payouts</div>
                      <div className="text-2xl font-bold text-neon-green">Same day</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-medex-surface/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              NEMT Payment Problems We <span className="text-neon-blue">Solve</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                  <point.icon className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-sm text-slate-400">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Everything You Need to Get Paid
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              From claims submission to driver payouts, Medex Pay handles the entire money flow.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card p-6 ${feature.highlight ? 'border-neon-green/50 bg-neon-green/5' : ''}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  feature.highlight ? 'bg-neon-green/20' : 'bg-neon-blue/10'
                }`}>
                  <feature.icon className={`w-6 h-6 ${feature.highlight ? 'text-neon-green' : 'text-neon-blue'}`} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Signup Form Section */}
      <section id="signup" className="py-20 bg-medex-surface/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
              Ready to Fix Your Cash Flow?
            </h2>
            <p className="text-lg text-slate-400">
              Tell us about your operation and we'll show you how Medex Pay can help.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="glass-card p-8"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Company Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Contact Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full input-dark"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Monthly Trip Volume
                </label>
                <select
                  name="monthly_trip_volume"
                  value={formData.monthly_trip_volume}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Select volume</option>
                  <option value="<500">Less than 500</option>
                  <option value="500-2000">500-2,000</option>
                  <option value="2000-5000">2,000-5,000</option>
                  <option value="5000-10000">5,000-10,000</option>
                  <option value="10000+">10,000+</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Current Billing Method
                </label>
                <select
                  name="current_billing_method"
                  value={formData.current_billing_method}
                  onChange={handleChange}
                  className="w-full input-dark"
                >
                  <option value="">Select method</option>
                  <option value="manual">Manual / Paper</option>
                  <option value="clearinghouse">Clearinghouse</option>
                  <option value="billing_service">Third-party Billing Service</option>
                  <option value="software">Billing Software</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Primary Payer Types (select all that apply)
              </label>
              <div className="flex flex-wrap gap-2">
                {payerTypes.map(payer => (
                  <button
                    key={payer}
                    type="button"
                    onClick={() => handleMultiSelect('primary_payer_types', payer)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      formData.primary_payer_types.includes(payer)
                        ? 'bg-neon-blue/20 border-neon-blue/50 text-neon-blue border'
                        : 'bg-white/5 border-white/10 text-slate-400 border hover:border-white/20'
                    }`}
                  >
                    {payer}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Features You're Most Interested In
              </label>
              <div className="flex flex-wrap gap-2">
                {featureInterests.map(feature => (
                  <button
                    key={feature}
                    type="button"
                    onClick={() => handleMultiSelect('interested_features', feature)}
                    className={`px-4 py-2 rounded-lg text-sm transition-all ${
                      formData.interested_features.includes(feature)
                        ? 'bg-neon-green/20 border-neon-green/50 text-neon-green border'
                        : 'bg-white/5 border-white/10 text-slate-400 border hover:border-white/20'
                    }`}
                  >
                    {feature}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Tell us about your biggest payment challenges
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className="w-full input-dark resize-none"
                placeholder="What's your biggest pain point with getting paid?"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <span>Request Demo</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default PaymentsPage;
